import { useState } from "react";
import { toast } from "react-toastify";
import Loader from "components/loader";
import { MEDICAL_PROFESSIONS } from "enums";
import { ScaleLoader } from "react-spinners";
import { getAllTemplates } from "api/templates";
import CustomDropdownProfession from "components/dropdown-profession";
import MobileHeader from "components/mobile-header";
import { updateUser, getUserByCogId } from "api/user";
import { Controller, useForm } from "react-hook-form";
import DashboardHeader from "components/dashboard-header";
import NotificationBar from "components/notification-bar";
import InputFieldLabel from "components/input-field-label";
import ChangePassword from "sections/profile/change-password";
import { useQueryClient, useMutation, useQuery } from "react-query";
import { ReactComponent as DropdownIcon } from "assets/svgs/dropdown.svg";
import { ReactComponent as CheckFilled } from "assets/svgs/check-filled.svg";
import useComponentClickOutside from "utils/hooks/use-component-click-outside";
import { ReactComponent as CheckUnfilled } from "assets/svgs/check-unfilled.svg";

const Profile = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const queryClient = useQueryClient();

  const { wrapRef, toggleChild, isComponentVisible } =
    useComponentClickOutside();

  const [loading, setLoading] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  const { data, isLoading } = useQuery("userProfile", getUserByCogId, {
    onSuccess: (successData) => {
      setSelectedTemplates(() => {
        const res = new Set(
          (typeof successData?.commonTemplate === "string"
            ? successData.commonTemplate
            : ""
          ).split(",")
        );
        const finalRes = Array.from(res);
        return finalRes;
      });
    },
  });

  const { data: outputTemplates, isLoading: isTemplatesLoading } = useQuery(
    "templates",
    getAllTemplates
  );

  const updateUserMutation = useMutation(updateUser, {
    onSuccess: () => {
      toast.success("Profile changes saved", {
        position: "top-right",
      });
      queryClient.refetchQueries("userProfile");
    },
    onError: async () => {
      toast.error("Error updating your profile", {
        position: "top-right",
      });
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const handleDropdownClick = (e) => {
    e.preventDefault();
    toggleChild();
  };

  const handleTemplateOptionsClick = (templateId) => {
    setSelectedTemplates((prev) => {
      const numberedArr = prev.map((el) => Number(el));

      if (numberedArr.includes(templateId)) {
        return numberedArr.filter((el) => el !== templateId);
      }

      const uniqueTemplates = new Set([...numberedArr, templateId]);
      const result = Array.from(uniqueTemplates);
      return result;
    });
  };

  const getTemplatePlaceholder = () => {
    if (selectedTemplates.length === 0) return "Choose a template";

    const result = selectedTemplates
      .map((el) => outputTemplates?.find((ele) => ele.id === Number(el))?.name)
      .join(", ");

    return result;
  };

  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      updateUserMutation.mutate({
        firstName: formData.firstName || data?.firstName || "",
        lastName: formData.lastName || data?.lastName || "",
        // TODO: pass id to the backend once the schema for profession table is changed
        profession:
          MEDICAL_PROFESSIONS.find((el) => el.id === formData.profession)
            ?.name ||
          data?.profession ||
          "",
        commonTemplate:
          selectedTemplates.join(",") || data?.commonTemplate || "",
        commonConditions:
          formData.commonConditions || data?.commonConditions || "",
      });
    } catch (error) {}
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="w-full px-4 md:px-6 lg:px-6">
      <MobileHeader />

      <div className="flex items-center justify-between border-b border-solid border-brand-header-border mb-4 mt-2 lg:mt-0 pb-4 lg:pb-2.5">
        <DashboardHeader label={"Profile"} className={"xl:max-w-[850px] w-full mt-4 lg:w-full"} />
        <NotificationBar />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="xl:max-w-[850px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-8">
          <div className="w-full">
            <InputFieldLabel
              isRequired={true}
              label={"First Name"}
              className={"mb-1.5"}
            />
            <input
              {...register("firstName", { required: "First Name is required" })}
              placeholder="First Name"
              defaultValue={(!isLoading && data?.firstName) || ""}
              className="py-2 px-[18px] input-field-classes w-full text-base"
            />

            <div className="h-7 flex flex-col items-start justify-start">
              {errors && errors.firstName && (
                <span className="text-red text-sm">
                  {errors.firstName.message}
                </span>
              )}
            </div>
          </div>

          <div className="w-full">
            <InputFieldLabel
              isRequired={true}
              label={"Last Name"}
              className={"mb-1.5"}
            />
            <input
              {...register("lastName", { required: "Last Name is required" })}
              placeholder="Last Name"
              defaultValue={(!isLoading && data?.lastName) || ""}
              className="py-2 px-[18px] input-field-classes w-full text-base"
            />
            <div className="h-7 flex flex-col items-start justify-start">
              {errors && errors.lastName && (
                <span className="text-red text-sm">
                  {errors.lastName.message}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mb-7">
          <InputFieldLabel
            label="Email"
            isRequired={true}
            className={"mb-1.5"}
          />
          <input
            readOnly={true}
            disabled={true}
            defaultValue={data?.email}
            {...register("email")}
            className="py-2 px-[18px] w-full input-field-classes text-base"
          />
        </div>

        <div>
          <InputFieldLabel
            isRequired={true}
            className={"mb-1.5"}
            label={"Profession"}
          />
          <Controller
            name="profession"
            control={control}
            rules={{ required: "Profession is required" }}
            defaultValue={
              data?.profession
                ? (MEDICAL_PROFESSIONS.find((el) => el.name === data.profession)?.id ?? null)
                : null
            }
            render={({ field: { onChange, value } }) => (
              <CustomDropdownProfession
                value={value}
                className="w-full"
                onSelect={onChange}
                options={MEDICAL_PROFESSIONS}
                placeholder={
                  MEDICAL_PROFESSIONS.find((el) => el.id === value)?.name || "Choose profession"
                }
              />
            )}
          />
          <div className="h-7 flex flex-col items-start justify-start">
            {errors && errors.profession && (
              <span className="text-red text-sm">
                {errors.profession.message}
              </span>
            )}
          </div>
        </div>

       {/*<div className="mb-7">
          <InputFieldLabel
            className={"mb-1.5"}
            label="Which patient documents or letters do you regularly create in your practice?"
          />
          <div
            ref={wrapRef}
            className={`relative ${isTemplatesLoading ? "select-none" : ""}`}
          >
            <button
              type="button"
              onClick={handleDropdownClick}
              disabled={isTemplatesLoading}
              className="w-full p-2 relative h-[53px] px-[15px] flex justify-start items-start input-field-classes overflow-hidden"
            >
              <span className="text-xl mr-2 select-none">
                {getTemplatePlaceholder()}
              </span>
              <DropdownIcon />
            </button>

            {isComponentVisible && (
              <ul className="absolute w-full rounded-lg bg-white left-0 top-[60px] shadow-secondary-field z-10 text-black max-h-48 overflow-y-auto">
                {outputTemplates.map(({ id, name }) => {
                  const isSelected = selectedTemplates.find(
                    (el) => Number(el) === id
                  );

                  return (
                    <li
                      key={id}
                      onClick={() => handleTemplateOptionsClick(id)}
                      className={`px-[18px] flex items-center gap-3 cursor-pointer py-3 hover:bg-grey2 transition-all duration-100 ease-in-out`}
                    >
                      {isSelected ? <CheckFilled /> : <CheckUnfilled />}
                      {name}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="mb-7">
          <InputFieldLabel
            label="What are some of the most common conditions or patients you see in your practice?"
            className={"mb-1.5"}
          />
          <textarea
            {...register("commonConditions")}
            defaultValue={data?.commonConditions}
            className="p-2 px-[18px] w-full h-24 input-field-classes text-xl"
          />
        </div>*/}

        <div className="flex justify-end mt-[18px]">
          <button
            type="submit"
            disabled={loading}
            className="bg-green border border-solid border-gray-600 rounded-lg p-2 text-base px-4 py-1 relative disabled:cursor-not-allowed"
          >
            <span className={loading ? "opacity-0" : "opacity-100"}>Save</span>
            {loading && (
              <ScaleLoader
                loading={loading}
                className="absolute top-[60%] left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            )}
          </button>
        </div>
      </form>

      <ChangePassword />
    </div>
  );
};

export default Profile;